import { useState } from "react";

import {
  AdminInviteRow,
  AdminUserRow,
  useAdminInvites,
  useAdminUsers,
  useIssueInvite,
  useRevokeInvite,
  useSetUserApproved,
} from "../api/admin";

const GOOGLE_CONSENT_URL = "https://console.cloud.google.com/apis/credentials/consent";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "—";
  const secs = (Date.now() - d) / 1000;
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86_400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 86_400 * 30) return `${Math.floor(secs / 86_400)}d ago`;
  return fmtDate(iso);
}

export default function AdminUsersSection() {
  const users = useAdminUsers();
  const invites = useAdminInvites();
  const issue = useIssueInvite();
  const revoke = useRevokeInvite();
  const setApproved = useSetUserApproved();

  const [emailInput, setEmailInput] = useState("");
  const [lastIssued, setLastIssued] = useState<string | null>(null);

  function onIssue(e: React.FormEvent) {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes("@")) return;
    issue.mutate(email, {
      onSuccess: () => {
        setEmailInput("");
        setLastIssued(email);
      },
    });
  }

  return (
    <section className="mt-6 rounded-2xl border border-amber-900/50 bg-slate-900/60 p-5">
      <div className="mb-4 flex items-center gap-2">
        <h2 className="text-base font-semibold text-slate-100">User invites</h2>
        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-400">
          admin
        </span>
      </div>

      {/* --- Issue invite form ------------------------------------------- */}
      <form onSubmit={onIssue} className="flex flex-wrap items-center gap-2">
        <input
          type="email"
          required
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          placeholder="alice@example.com"
          autoComplete="off"
          className="min-w-[16rem] flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-sky-500"
        />
        <button
          type="submit"
          disabled={issue.isPending || !emailInput.trim()}
          className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-sky-400 active:bg-sky-600 disabled:opacity-50"
        >
          {issue.isPending ? "Issuing…" : "Issue invite"}
        </button>
      </form>

      {issue.isError && (
        <p className="mt-2 text-xs text-rose-300">Failed: {issue.error.message}</p>
      )}

      {lastIssued && issue.isSuccess && (
        <div className="mt-3 rounded-lg border border-amber-800/60 bg-amber-950/40 p-3 text-xs text-amber-200">
          <p className="font-medium">Invited {lastIssued}.</p>
          <p className="mt-1 text-amber-200/80">
            Reminder: if the OAuth consent screen is still in <em>Testing</em> mode, also add this
            email to{" "}
            <a
              href={GOOGLE_CONSENT_URL}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-dotted underline-offset-2 hover:text-amber-100"
            >
              Google Cloud test users
            </a>
            {" "}before they can sign in.
          </p>
        </div>
      )}

      {/* --- Pending invites --------------------------------------------- */}
      <div className="mt-6">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Pending invites
        </h3>
        {invites.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : invites.data && invites.data.rows.length > 0 ? (
          <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800">
            {invites.data.rows.map((inv) => (
              <PendingInviteRow
                key={inv.email}
                invite={inv}
                onRevoke={() => {
                  if (confirm(`Revoke invite for ${inv.email}?`)) revoke.mutate(inv.email);
                }}
                pending={revoke.isPending && revoke.variables === inv.email}
              />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No pending invites.</p>
        )}
      </div>

      {/* --- Users table ------------------------------------------------- */}
      <div className="mt-6">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
          Users ({users.data?.rows.length ?? 0})
        </h3>
        {users.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : users.data && users.data.rows.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-900/80 text-[11px] uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Joined</th>
                  <th className="px-3 py-2">Last login</th>
                  <th className="px-3 py-2">Watchlist</th>
                  <th className="px-3 py-2">Re-analyze<br />today</th>
                  <th className="px-3 py-2">Telegram</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {users.data.rows.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    onToggleApproved={() =>
                      setApproved.mutate({ id: u.id, approved: !u.approved })
                    }
                    onResetInvite={() => {
                      if (confirm(`Re-issue invite for ${u.email}?`)) issue.mutate(u.email);
                    }}
                    pending={
                      (setApproved.isPending && setApproved.variables?.id === u.id) ||
                      (issue.isPending && emailInput === "" && lastIssued === u.email)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No users yet.</p>
        )}
      </div>
    </section>
  );
}

function PendingInviteRow({
  invite,
  onRevoke,
  pending,
}: {
  invite: AdminInviteRow;
  onRevoke: () => void;
  pending: boolean;
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
      <div className="min-w-0 flex-1">
        <span className="font-medium text-slate-100">{invite.email}</span>
        <span className="ml-2 text-xs text-slate-500">
          invited {relTime(invite.invited_at)}
          {invite.invited_by_email ? ` by ${invite.invited_by_email}` : ""}
        </span>
      </div>
      <button
        onClick={onRevoke}
        disabled={pending}
        className="rounded-md border border-rose-900 px-2 py-1 text-xs text-rose-300 transition hover:bg-rose-950/40 disabled:opacity-50"
      >
        {pending ? "Revoking…" : "Revoke"}
      </button>
    </li>
  );
}

function UserRow({
  user,
  onToggleApproved,
  onResetInvite,
  pending,
}: {
  user: AdminUserRow;
  onToggleApproved: () => void;
  onResetInvite: () => void;
  pending: boolean;
}) {
  const tierTotal = user.live + user.tracked + user.idle;
  return (
    <tr className="text-slate-200">
      <td className="px-3 py-2 font-medium">{user.email}</td>
      <td className="px-3 py-2 text-slate-400">{fmtDate(user.created_at)}</td>
      <td className="px-3 py-2 text-slate-400">{relTime(user.last_login_at)}</td>
      <td className="px-3 py-2 text-xs">
        {tierTotal === 0 ? (
          <span className="text-slate-500">—</span>
        ) : (
          <span title={`live ${user.live} · tracked ${user.tracked} · idle ${user.idle}`}>
            <span className="text-emerald-400">{user.live}</span>
            <span className="text-slate-500"> / </span>
            <span className="text-sky-400">{user.tracked}</span>
            <span className="text-slate-500"> / </span>
            <span className="text-slate-400">{user.idle}</span>
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-slate-300">{user.reanalyze_used_today}</td>
      <td className="px-3 py-2 text-xs">
        {user.telegram_paired ? (
          <span className="text-emerald-400">paired</span>
        ) : (
          <span className="text-slate-500">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {user.approved ? (
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-400">
            active
          </span>
        ) : (
          <span className="rounded-full bg-rose-500/15 px-2 py-0.5 font-medium text-rose-300">
            disabled
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex flex-wrap justify-end gap-1">
          <button
            onClick={onToggleApproved}
            disabled={pending}
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 transition hover:bg-slate-800 disabled:opacity-50"
          >
            {user.approved ? "Disable" : "Re-enable"}
          </button>
          <button
            onClick={onResetInvite}
            disabled={pending}
            title="Re-issue invite (clears used_at) so this user can sign in again with a fresh OAuth identity."
            className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 transition hover:bg-slate-800 disabled:opacity-50"
          >
            Reset invite
          </button>
        </div>
      </td>
    </tr>
  );
}
